import { useState, useEffect } from "react";
import { Camera, Video, ExternalLink } from "lucide-react";
import { API } from "@/lib/api";

function isImage(url = "") {
  return /\.(jpe?g|png|gif|webp|bmp)(\?.*)?$/i.test(url);
}
function isEmbed(url = "") {
  return /(embed|player|\/e\/|iframe)/i.test(url);
}

export function WebcamCard({ spot }) {
  const url = spot?.webcam_url;
  const [tick, setTick] = useState(Date.now());

  useEffect(() => {
    if (!url || !isImage(url)) return;
    const id = setInterval(() => setTick(Date.now()), 20000);
    return () => clearInterval(id);
  }, [url]);

  const imgSrc = url ? `${API}/webcam-proxy?url=${encodeURIComponent(url)}&t=${tick}` : "";

  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden" data-testid="webcam-card">
      <div className="flex items-center gap-2 px-5 pt-5 pb-3">
        <div className="h-8 w-8 rounded-xl bg-sky-100 flex items-center justify-center">
          <Video className="h-4 w-4 text-sky-600" />
        </div>
        <h3 className="font-head text-xl font-semibold text-slate-900">Webcam · {spot?.name}</h3>
        {url && (
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-red-500">
            <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" /> Live
          </span>
        )}
      </div>

      {!url ? (
        <div className="aspect-video w-full flex flex-col items-center justify-center bg-sky-50 text-slate-400" data-testid="webcam-empty">
          <Camera className="h-10 w-10 mb-2 text-sky-200" />
          <p className="text-sm font-medium">Nessuna webcam per questo spot</p>
          <p className="text-xs">Seba può aggiungerla dal pannello</p>
        </div>
      ) : isImage(url) ? (
        <a href={url} target="_blank" rel="noopener noreferrer" className="relative block w-full bg-slate-900 aspect-video group">
          <img key={tick} src={imgSrc} alt={`Webcam ${spot?.name}`} className="w-full h-full object-cover" data-testid="webcam-image" />
          <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-black/55 text-white text-xs font-semibold px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            Apri a schermo intero <ExternalLink className="h-3.5 w-3.5" />
          </span>
        </a>
      ) : isEmbed(url) ? (
        <div className="relative w-full bg-slate-900 aspect-video">
          <iframe src={url} title={`Webcam ${spot?.name}`} className="w-full h-full" allow="autoplay; fullscreen" allowFullScreen data-testid="webcam-iframe" />
        </div>
      ) : (
        <a href={url} target="_blank" rel="noopener noreferrer" data-testid="webcam-link" className="group relative block aspect-video w-full overflow-hidden">
          {spot?.image_url ? (
            <img src={spot.image_url} alt={spot?.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-sky-600 to-cyan-500" />
          )}
          <div className="absolute inset-0 bg-slate-900/45 group-hover:bg-slate-900/30 transition-colors duration-300" />
          <div className="relative z-10 h-full flex flex-col items-center justify-center text-white gap-3">
            <div className="h-14 w-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <Video className="h-7 w-7" />
            </div>
            <span className="font-head font-bold text-lg inline-flex items-center gap-2">
              Guarda la webcam live <ExternalLink className="h-4 w-4" />
            </span>
            <span className="text-xs text-white/70">si apre in una nuova scheda</span>
          </div>
        </a>
      )}
    </div>
  );
}
